import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "ui/Carousel";
import AlignmentFormContent from "components/Alignment/AlignmentFormContent";
import type { QuizDetail } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import type { QuizResponseInput } from "lib/api";
import { formatQuizTemplate, getQuizButtonClassName } from "./quizUtils";

interface QuizPart2ResponsesProps {
  ui: QuizUiStrings;
  quiz: QuizDetail;
  onSubmit: (responses: QuizResponseInput[]) => Promise<void>;
  isSaving: boolean;
}

interface LocalResponseState {
  quizTeabateIds: number[];
  response: number | null;
  importance: number | null;
}

const RESPONSE_OPTIONS = [-2, -1, 0, 1, 2] as const;
const IMPORTANCE_OPTIONS = [-2, -1, 0, 1, 2] as const;

/**
 * Anonymous teabate response round for part two.
 */
const QuizPart2Responses: React.FC<QuizPart2ResponsesProps> = ({
  ui,
  quiz,
  onSubmit,
  isSaving,
}) => {
  const cards = quiz.part2.anonymous_teabates;
  const initialState = useMemo(() => {
    const entries = new Map<number, LocalResponseState>();
    cards.forEach((card) => {
      const existingStance = quiz.viewer?.stances.find((stance) =>
        stance.quiz_teabate_id !== null && card.quiz_teabate_ids.includes(stance.quiz_teabate_id)
      );
      if (card.post_id) {
        entries.set(card.post_id, {
          quizTeabateIds: card.quiz_teabate_ids,
          response: existingStance?.response ?? null,
          importance: existingStance?.importance ?? null,
        });
      }
    });
    return entries;
  }, [cards, quiz.viewer?.stances]);

  const [responses, setResponses] = useState<Map<number, LocalResponseState>>(initialState);
  const hasAutoSubmittedRef = useRef(false);

  useEffect(() => {
    setResponses(initialState);
  }, [initialState]);

  useEffect(() => {
    if (cards.length === 0 && !hasAutoSubmittedRef.current && !isSaving) {
      hasAutoSubmittedRef.current = true;
      void onSubmit([]);
    }
  }, [cards.length, isSaving, onSubmit]);

  if (cards.length === 0) {
    return (
      <div className="mx-auto flex w-full max-w-3xl flex-col items-center gap-3 px-4 py-20 text-center">
        <Loader2 className="size-6 animate-spin text-eoe-blue" aria-hidden />
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.loadingQuiz}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.part2ResponsesTitle}
        </h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.part2ResponsesDescription}</p>
      </div>

      <Carousel className="w-full">
        <CarouselContent>
          {cards.map((card) => {
            const localState = card.post_id ? responses.get(card.post_id) : null;
            return (
              <CarouselItem key={card.post_id ?? card.issue_id}>
                <div className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
                  <div className="mb-5 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-full bg-eoe-icon px-2 py-1 text-xs text-eoe-text-sub dark:bg-eoe-dark-icon dark:text-eoe-dark-text-sub">
                        {card.issue.name}
                      </span>
                      <span className="rounded-full bg-eoe-blue/10 px-2 py-1 text-xs text-eoe-blue dark:bg-eoe-blue/20">
                        {formatQuizTemplate(ui.selectedByCountLabel, { count: card.selection_count })}
                      </span>
                    </div>
                    {card.post.body ? (
                      <p className="whitespace-pre-wrap text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        {card.post.body}
                      </p>
                    ) : null}
                  </div>
                  <AlignmentFormContent
                    teabateTitle={card.post.title}
                    dimension={typeof card.post.properties?.dimension === "string" ? card.post.properties.dimension : null}
                    response={localState?.response ?? null}
                    importance={localState?.importance ?? null}
                    onResponseChange={(value) => {
                      const postId = card.post_id;
                      if (!postId) {
                        return;
                      }
                      setResponses((current) => {
                        const next = new Map(current);
                        const existing = next.get(postId) || {
                          quizTeabateIds: card.quiz_teabate_ids,
                          response: null,
                          importance: null,
                        };
                        next.set(postId, {
                          ...existing,
                          response: value,
                        });
                        return next;
                      });
                    }}
                    onImportanceChange={(value) => {
                      const postId = card.post_id;
                      if (!postId) {
                        return;
                      }
                      setResponses((current) => {
                        const next = new Map(current);
                        const existing = next.get(postId) || {
                          quizTeabateIds: card.quiz_teabate_ids,
                          response: null,
                          importance: null,
                        };
                        next.set(postId, {
                          ...existing,
                          importance: value,
                        });
                        return next;
                      });
                    }}
                    claimLabel={ui.createClaimLabel}
                    responseLabel={ui.stanceLabel}
                    importanceLabel={ui.importanceLabel}
                    responseOptions={RESPONSE_OPTIONS.map((value) => ({
                      value,
                      label:
                        value === -2
                          ? ui.responseStronglyDisagree
                          : value === -1
                            ? ui.responseDisagree
                            : value === 0
                              ? ui.responseNeutral
                              : value === 1
                                ? ui.responseAgree
                                : ui.responseStronglyAgree,
                    }))}
                    importanceOptions={IMPORTANCE_OPTIONS.map((value) => ({
                      value,
                      label:
                        value === -2
                          ? ui.importanceVeryLow
                          : value === -1
                            ? ui.importanceLow
                            : value === 0
                              ? ui.importanceNeutral
                              : value === 1
                                ? ui.importanceHigh
                                : ui.importanceVeryHigh,
                    }))}
                  />
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <div className="mt-4 flex items-center justify-between">
          <CarouselPrevious hideWhenDisabled="invisible" />
          <CarouselDots />
          <CarouselNext hideWhenDisabled="invisible" />
        </div>
      </Carousel>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() =>
            void onSubmit(
              Array.from(responses.values())
                .filter((entry) => entry.response !== null && entry.importance !== null)
                .map((entry) => ({
                  quizTeabateIds: entry.quizTeabateIds,
                  response: entry.response as number,
                  importance: entry.importance as number,
                }))
            )
          }
          disabled={
            isSaving ||
            Array.from(responses.values()).some((entry) => entry.response === null || entry.importance === null)
          }
          className={getQuizButtonClassName({
            disabled: Boolean(
              isSaving ||
              Array.from(responses.values()).some((entry) => entry.response === null || entry.importance === null)
            ),
          })}
        >
          {ui.submitResponsesAction}
        </button>
      </div>
    </div>
  );
};

export default QuizPart2Responses;
